import { ChevronLeft } from 'lucide-react'

/** Full-panel detail overlay (mock `.detail-layer`): sticky head (Back + ref), centered scroll body,
 *  optional sticky footer CTA. Mounted by AnalyticsPanel when the detail stack is non-empty. */
export function DetailOverlay({
  backLabel,
  onBack,
  refText,
  footer,
  children,
}: {
  backLabel: string
  onBack: () => void
  refText?: string | undefined
  footer?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="absolute inset-0 z-30 flex min-h-0 flex-col bg-gis-panel">
      <div className="flex flex-none justify-center border-b border-gis-line bg-white px-[22px] py-[10px]">
        <div className="mx-auto flex w-full max-w-[900px] items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-[5px] rounded-[9px] py-[7px] pr-3 pl-2 text-[12px] font-semibold text-gis-ink-2 transition hover:bg-[#f1f4f9] hover:text-gis-ink focus-visible:ring-2 focus-visible:ring-blue-500/60"
          >
            <ChevronLeft className="h-[17px] w-[17px] stroke-[2.2]" />
            {backLabel}
          </button>
          {refText && (
            <span className="ml-auto flex-none text-[11px] font-semibold tracking-[.02em] text-gis-muted tabular-nums">
              {refText}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center overflow-x-hidden overflow-y-auto px-6 pt-5 pb-[26px] [&>*]:w-full [&>*]:max-w-[900px]">
        {children}
      </div>

      {footer && (
        <div className="flex flex-none justify-center border-t border-gis-line bg-white px-[22px] py-[10px]">
          <div className="mx-auto flex w-full max-w-[900px] justify-end gap-[10px]">{footer}</div>
        </div>
      )}
    </div>
  )
}

interface DetailShellProps {
  backLabel: string
  onBack: () => void
  kicker: string
  name: string
  chip: { text: string; bg: string; color: string }
  children: React.ReactNode
}

/** Shared frame for the three detail modes: back row, kicker, name, status chip. */
export function DetailShell({ backLabel, onBack, kicker, name, chip, children }: DetailShellProps) {
  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-2.5 rounded-lg border border-slate-900/10 bg-white px-2.5 py-1 text-xs font-semibold hover:bg-slate-100"
      >
        ← {backLabel}
      </button>
      <div className="text-muted-text text-[10px] font-bold tracking-widest uppercase">{kicker}</div>
      <div className="mt-0.5 mb-1.5 text-xl font-extrabold">{name}</div>
      <span
        className="inline-block rounded-full px-2 py-0.5 text-[10.5px] font-bold"
        style={{ background: chip.bg, color: chip.color }}
      >
        {chip.text}
      </span>
      {children}
    </div>
  )
}

export function DetailSection({
  title,
  children,
  action,
}: {
  title: string
  children: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <div className="mt-4 rounded-[14px] border border-slate-900/10 bg-white p-3">
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <h5 className="text-muted-text text-[10.5px] tracking-wide uppercase">{title}</h5>
        {action}
      </div>
      {children}
    </div>
  )
}

export function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="my-1.5 flex justify-between text-[12px]">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  )
}

export function BarTrack({ pct, accent = '#2563eb' }: { pct: number; accent?: string }) {
  return (
    <div className="mt-1 h-[7px] overflow-hidden rounded-full bg-[#eef2f7]">
      <i className="block h-full" style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: accent }} />
    </div>
  )
}

export function ListRow({ left, right, onClick }: { left: string; right: string; onClick?: () => void }) {
  const cls = 'flex w-full items-center justify-between border-b border-slate-100 py-1.5 text-[12px] last:border-b-0'
  return onClick ? (
    <button type="button" onClick={onClick} className={`${cls} text-left hover:bg-slate-50`}>
      <span>{left}</span>
      <span>{right}</span>
    </button>
  ) : (
    <div className={cls}>
      <span>{left}</span>
      <span>{right}</span>
    </div>
  )
}

/** Layout-mirroring skeleton for a detail drawer body (photo, title, badges, people card, glance, list). */
export function DetailOverlaySkeleton() {
  const block = 'animate-pulse rounded-[10px] bg-gis-line-2'
  return (
    <div className="space-y-4">
      <div className={`${block} h-[150px] rounded-[14px]`} />
      <div className={`${block} h-7 w-3/4`} />
      <div className="flex gap-2">
        <div className={`${block} h-6 w-20 rounded-full`} />
        <div className={`${block} h-6 w-16 rounded-full`} />
        <div className={`${block} h-6 w-24 rounded-full`} />
      </div>
      <div className={`${block} h-[120px] rounded-2xl`} />
      <div className={`${block} h-[110px] rounded-2xl`} />
      <div className="space-y-2">
        <div className={`${block} h-12`} />
        <div className={`${block} h-12`} />
      </div>
    </div>
  )
}
